// src/Home.js
import React, { useState, useEffect, useRef, useContext, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { FaWhatsapp, FaFacebook, FaInstagram } from "react-icons/fa";
import { ChevronLeft, ChevronRight, ShoppingCart, Heart } from "lucide-react";
import axios from "axios";
import hero1 from "./assets/slid1.jpg";
import hero2 from "./assets/slid2.webp";
import hero3 from "./assets/slid3.jpg";
import hero4 from "./assets/xcb.jpg";
import Cart from "./components/Cart";
import Wishlist from "./components/Wishlist";
import QuickShop from "./components/QuickShop";
import { CartContext, CartProvider } from "./components/CartContext";
import { API_BASE_URL } from "./config";

const heroSlides = [
  {
    img: hero1,
    title: "Light Up Diwali with Sparkle!",
    desc: "Celebrate the festival of lights with our premium range of eco-friendly crackers.",
    btn: { text: "Shop Now", link: "#products" },
  },
  {
    img: hero2,
    title: "Safe & Vibrant Firecrackers",
    desc: "Explore our dazzling collection designed for joy and safety this festive season.",
    btn: { text: "Discover More", link: "#products" },
  },
  {
    img: hero3,
    title: "Festive Fireworks Extravaganza",
    desc: "Unleash the magic of Diwali with our high-quality, colorful crackers.",
    btn: { text: "View Collection", link: "#products" },
  },
  {
    img: hero4,
    title: "Bulk Orders at Wholesale Price",
    desc: "Get the best wholesale rates for bulk orders. Direct from Sivakasi!",
    btn: { text: "Order Bulk", link: "#products" },
  },
];

const HomePage = () => {
  const navigate = useNavigate();
  const { 
    cartItems, 
    addToCart, 
    searchTerm, 
    setSearchTerm, 
    selectedCategory, 
    setSelectedCategory,
    wishlist,
    toggleWishlist,
    isWishlistOpen,
    setIsWishlistOpen
  } = useContext(CartContext);

  const [products, setProducts] = useState([]);
  const [viewMode, setViewMode] = useState("grid"); // grid, table
  const [banners, setBanners] = useState([]);
  const [heroSlide, setHeroSlide] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isQuickShopOpen, setIsQuickShopOpen] = useState(false);
  const [notification, setNotification] = useState("");
  const [isUpdating, setIsUpdating] = useState(false); // Lock for click handling
  const canvasRef = useRef(null);

  // Define showNotification to fix no-undef errors
  const showNotification = (message, duration = 3000) => {
    setNotification(message);
    setTimeout(() => setNotification(""), duration);
  };

  // Fetch products and banners on mount
  useEffect(() => {
    // Fetch products
    axios
      .get(`${API_BASE_URL}/all-data`)
      .then((response) => {
        console.log("Fetched products:", response.data);
        setProducts(response.data || []);
        setIsLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching products:", error);
        setError("Failed to load products. Please try again later.");
        setIsLoading(false);
      });

    // Fetch active banners
    axios
      .get(`${API_BASE_URL}/banners`)
      .then((response) => {
        if (response.data && response.data.length > 0) {
          setBanners(response.data);
        }
      })
      .catch((err) => {
        console.error("Error fetching banners:", err);
      });
  }, []);

  // Canvas firework backdrop effect
  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!ctx) return;

    let particles = [];
    let explosionInterval;

    const createParticle = (x, y) => {
      const colors = ["#FFD700", "#FF4500", "#00FF00", "#FF69B4", "#00BFFF"];
      return {
        x,
        y,
        radius: Math.random() * 3 + 1,
        color: colors[Math.floor(Math.random() * colors.length)],
        vx: (Math.random() - 0.5) * 10,
        vy: (Math.random() - 0.5) * 10,
        alpha: 1,
      };
    };

    const createExplosion = () => {
      const x = Math.random() * canvas.width;
      const y = Math.random() * canvas.height;
      for (let i = 0; i < 30; i++) {
        particles.push(createParticle(x, y));
      }
    };

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particles = particles.filter((p) => p.alpha > 0);
      particles.forEach((p) => {
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.radius, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${parseInt(p.color.slice(1, 3), 16)}, ${parseInt(
          p.color.slice(3, 5),
          16
        )}, ${parseInt(p.color.slice(5, 7), 16)}, ${p.alpha})`;
        ctx.fill();
        p.x += p.vx;
        p.y += p.vy;
        p.vx *= 0.95;
        p.vy *= 0.95;
        p.alpha -= 0.02;
      });
      requestAnimationFrame(animate);
    };

    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    resizeCanvas();
    window.addEventListener("resize", resizeCanvas);
    explosionInterval = setInterval(createExplosion, 1000);
    const animationFrameId = requestAnimationFrame(animate);

    return () => {
      window.removeEventListener("resize", resizeCanvas);
      clearInterval(explosionInterval);
      cancelAnimationFrame(animationFrameId);
    };
  }, []);

  // Map active slides dynamically (Must be declared before being referenced in effects or click handlers)
  const activeSlides = banners.length > 0 
    ? banners.map((b) => ({
        img: b.image_url || b.IMAGE_URL,
        title: b.title || b.TITLE,
        desc: b.description || b.DESCRIPTION,
        btn: { text: "Shop Now", link: "#products" },
      }))
    : heroSlides;

  // Auto rotate banner effect - 5 seconds per slide for readable content display
  useEffect(() => {
    if (activeSlides.length <= 1) return;
    const interval = setInterval(() => {
      setHeroSlide((prev) => (prev + 1) % activeSlides.length);
    }, 5000); // Auto move every 5 seconds - gives users time to read
    return () => clearInterval(interval);
  }, [activeSlides.length]);

  const nextHeroSlide = () => setHeroSlide((prev) => (prev + 1) % activeSlides.length);
  const prevHeroSlide = () => setHeroSlide((prev) => (prev - 1 + activeSlides.length) % activeSlides.length);

  const categories = ["All", ...new Set(products.map((p) => p.name).filter(Boolean))];

  const filteredProducts = products.filter((product) => {
    const matchesSearch =
      product.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (product.subProducts || product.subproducts || []).some((sub) =>
        (sub.subName || sub.subname || "").toLowerCase().includes(searchTerm.toLowerCase())
      );

    const matchesCategory =
      selectedCategory === "All" ||
      product.name?.toLowerCase() === selectedCategory.toLowerCase();

    return matchesSearch && matchesCategory;
  });

  // ======== CONFETTI + SOUND EFFECT ========
  const playAddToCartEffect = useCallback((x, y, productName) => {
    // Play sound using Web Audio API
    try {
      const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioCtx.createOscillator();
      const gainNode = audioCtx.createGain();
      oscillator.connect(gainNode);
      gainNode.connect(audioCtx.destination);
      oscillator.type = "sine";
      oscillator.frequency.setValueAtTime(880, audioCtx.currentTime);
      oscillator.frequency.exponentialRampToValueAtTime(1320, audioCtx.currentTime + 0.08);
      oscillator.frequency.exponentialRampToValueAtTime(660, audioCtx.currentTime + 0.15);
      gainNode.gain.setValueAtTime(0.15, audioCtx.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioCtx.currentTime + 0.3);
      oscillator.start(audioCtx.currentTime);
      oscillator.stop(audioCtx.currentTime + 0.3);
    } catch (e) { /* Audio not supported */ }

    // Create confetti particles
    const colors = ["#FFD700", "#FF4500", "#00FF00", "#FF69B4", "#00BFFF", "#FFA500", "#FF0000", "#FF00FF"];
    for (let i = 0; i < 24; i++) {
      const particle = document.createElement("div");
      particle.className = "confetti-particle";
      const size = Math.random() * 8 + 4;
      particle.style.cssText = `
        position: fixed;
        width: ${size}px;
        height: ${size}px;
        background: ${colors[Math.floor(Math.random() * colors.length)]};
        border-radius: ${Math.random() > 0.5 ? "50%" : "2px"};
        pointer-events: none;
        z-index: 9999;
        left: ${x}px;
        top: ${y}px;
        --tx: ${(Math.random() - 0.5) * 300}px;
        --ty: ${(Math.random() - 0.5) * 300}px;
        --r: ${Math.random() * 720}deg;
        animation: confetti-burst 0.9s ease-out forwards;
        animation-delay: ${Math.random() * 0.1}s;
      `;
      document.body.appendChild(particle);
      setTimeout(() => particle.remove(), 1200);
    }
  }, []);

  const handleQuantityIncrease = (proId, subId, event) => {
    if (isUpdating) return; // Prevent multiple clicks
    setIsUpdating(true);

    const product = products.find((p) => p.pro_id === proId);
    if (!product) {
      showNotification("Product not found!", 3000);
      setIsUpdating(false);
      return;
    }
    const subProduct = (product.subProducts || product.subproducts || []).find((s) => s.sub_id === subId);
    if (!subProduct) {
      showNotification("Sub-product not found!", 3000);
      setIsUpdating(false);
      return;
    }
    const currentItem = cartItems.find(
      (item) => item.proId === proId && item.subId === subId
    );
    const newQuantity = (currentItem?.quantity || 0) + 1;

    const rates = subProduct.rateEntities || subProduct.rateentities || [];
    const rateObj = rates[0] || {};
    const rate = rateObj.rate || 0;
    const discount = rateObj.Discount !== undefined ? rateObj.Discount : (rateObj.discount || 0);

    const itemToAdd = {
      proId,
      subId,
      name: subProduct.subName || "Unknown Product",
      quantity: newQuantity,
      rate,
      discount,
    };
    addToCart(itemToAdd);
    // Trigger confetti + sound effect
    if (event && event.clientX) {
      playAddToCartEffect(event.clientX, event.clientY, subProduct.subName);
    } else {
      playAddToCartEffect(window.innerWidth / 2, window.innerHeight / 2, subProduct.subName);
    }
    showNotification(`${subProduct.subName} quantity increased to ${newQuantity}!`, 2000);
    setTimeout(() => setIsUpdating(false), 200);
  };

  const handleQuantityDecrease = (proId, subId) => {
    if (isUpdating) return;
    setIsUpdating(true);

    const product = products.find((p) => p.pro_id === proId);
    if (!product) {
      showNotification("Product not found!", 3000);
      setIsUpdating(false);
      return;
    }
    const subProduct = (product.subProducts || product.subproducts || []).find((s) => s.sub_id === subId);
    if (!subProduct) {
      showNotification("Sub-product not found!", 3000);
      setIsUpdating(false);
      return;
    }
    const currentItem = cartItems.find(
      (item) => item.proId === proId && item.subId === subId
    );
    if (!currentItem || currentItem.quantity <= 0) {
      showNotification("Cannot decrease quantity below 0!", 3000);
      setIsUpdating(false);
      return;
    }
    const newQuantity = currentItem.quantity - 1;
    const rates = subProduct.rateEntities || subProduct.rateentities || [];
    const rateObj = rates[0] || {};
    const rate = rateObj.rate || 0;
    const discount = rateObj.Discount !== undefined ? rateObj.Discount : (rateObj.discount || 0);

    const itemToAdd = {
      proId,
      subId,
      name: subProduct.subName || "Unknown Product",
      quantity: newQuantity,
      rate,
      discount,
    };
    addToCart(itemToAdd);
    showNotification(`${subProduct.subName} quantity decreased to ${newQuantity}!`, 2000);
    setTimeout(() => setIsUpdating(false), 200);
  };

  const handleQuantityChange = (proId, subId, newQty) => {
    if (isUpdating) return;
    setIsUpdating(true);

    const product = products.find((p) => p.pro_id === proId);
    if (!product) {
      showNotification("Product not found!", 3000);
      setIsUpdating(false);
      return;
    }
    const subProduct = (product.subProducts || product.subproducts || []).find((s) => s.sub_id === subId);
    if (!subProduct) {
      showNotification("Sub-product not found!", 3000);
      setIsUpdating(false);
      return;
    }

    // Validate input
    const parsedQty = parseInt(newQty, 10);
    if (isNaN(parsedQty) || parsedQty < 0) {
      showNotification("Please enter a valid quantity (0 or higher)!", 3000);
      setIsUpdating(false);
      return;
    }

    const rates = subProduct.rateEntities || subProduct.rateentities || [];
    const rateObj = rates[0] || {};
    const rate = rateObj.rate || 0;
    const discount = rateObj.Discount !== undefined ? rateObj.Discount : (rateObj.discount || 0);

    const itemToAdd = {
      proId,
      subId,
      name: subProduct.subName || "Unknown Product",
      quantity: parsedQty,
      rate,
      discount,
    };
    addToCart(itemToAdd);
    showNotification(`${subProduct.subName} quantity set to ${parsedQty}!`, 2000);
    setTimeout(() => setIsUpdating(false), 200);
  };

  const getCategoryEmoji = (name) => {
    const lower = name.toLowerCase();
    if (lower.includes("rocket")) return "🚀";
    if (lower.includes("sparkler")) return "✨";
    if (lower.includes("chakkar") || lower.includes("chakar")) return "🌀";
    if (lower.includes("flower") || lower.includes("pot")) return "🌋";
    if (lower.includes("bomb")) return "💣";
    if (lower.includes("combo")) return "🎁";
    if (lower.includes("gift") || lower.includes("box")) return "📦";
    if (lower.includes("kid")) return "🧸";
    if (lower.includes("fancy")) return "✨";
    return "🎇";
  };

  // Get first product image for a category
  const getCategoryImage = (catName) => {
    if (catName === "All") return null;
    const product = products.find((p) => p.name?.toLowerCase() === catName.toLowerCase());
    if (!product) return null;
    const subs = product.subProducts || product.subproducts || [];
    if (subs.length === 0) return null;
    return subs[0]?.childimg || null;
  };

  return (
    <div className="bg-[#FAF8F5] text-gray-800 font-sans relative min-h-screen pb-12 select-none">

      {/* Notification Popup */}
      {notification && (
        <motion.div
          role="alert"
          aria-live="assertive"
          initial={{ opacity: 0, y: -50 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -50 }}
          className="fixed top-4 left-1/2 transform -translate-x-1/2 bg-green-600 text-white px-6 py-3.5 rounded-xl shadow-xl z-[1500] font-bold text-sm"
        >
          {notification}
        </motion.div>
      )}

      {/* HERO BANNER SECTION */}
      <section className="relative w-full aspect-video sm:aspect-[25/9] overflow-hidden bg-slate-950">
        <canvas
          ref={canvasRef}
          className="absolute inset-0 z-10 pointer-events-none"
          style={{ opacity: 0.65 }}
        />
        <AnimatePresence initial={false}>
          {activeSlides.map((s, idx) =>
            idx === heroSlide ? (
              <motion.div
                key={idx}
                initial={{ opacity: 0, scale: 1.05 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.8 }}
                className="absolute inset-0 z-0 flex items-center justify-between px-6 sm:px-12 md:px-20 text-white"
                style={{
                  backgroundImage: `linear-gradient(to right, rgba(0,0,0,0.85) 45%, rgba(0,0,0,0.3) 100%), url(${s.img})`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  backgroundRepeat: "no-repeat",
                }}
              >
                {/* Left Text Column */}
                <div className="max-w-xl space-y-4 md:space-y-6 z-20 text-left">
                  <h2 className="text-3xl sm:text-5xl md:text-6xl font-black mb-2 text-yellow-400 font-serif leading-tight">
                    {s.title}
                  </h2>
                  <p className="text-sm sm:text-lg md:text-xl text-gray-200 font-semibold leading-relaxed">
                    {s.desc}
                  </p>

                  {/* Checklist row */}
                  <div className="grid grid-cols-2 gap-2 text-[10px] sm:text-xs font-bold text-gray-300">
                    <span className="flex items-center gap-1.5">✔️ 100% Original Sivakasi</span>
                    <span className="flex items-center gap-1.5">✔️ Safe Damage-Free Packaging</span>
                    <span className="flex items-center gap-1.5">✔️ Fast Delivery On-Time</span>
                    <span className="flex items-center gap-1.5">✔️ Secure Payments</span>
                  </div>

                  <a
                    href="#products"
                    className="inline-flex items-center gap-2 bg-yellow-400 hover:bg-yellow-500 text-red-950 px-6 sm:px-8 py-3 rounded-full font-black text-xs sm:text-sm uppercase tracking-wider transition-all shadow-md transform hover:scale-105"
                  >
                    <span>Shop Now</span> ➔
                  </a>
                </div>

                {/* Right Badge Graphic Column */}
                <div className="hidden lg:flex flex-col items-center gap-4 z-20 pr-6 animate-pulse">
                  <div className="w-36 h-36 rounded-full border-4 border-dashed border-yellow-400 bg-red-800/90 flex flex-col items-center justify-center shadow-2xl">
                    <span className="text-[10px] uppercase font-black tracking-widest text-gray-200">UP TO</span>
                    <span className="text-4xl font-extrabold text-white font-serif">80%</span>
                    <span className="text-xs font-black text-yellow-400 uppercase tracking-widest">OFF</span>
                  </div>
                  <div className="bg-yellow-400 text-red-950 text-xs font-black uppercase py-1.5 px-4 rounded-md tracking-wider shadow">
                    Biggest Diwali Sale
                  </div>
                </div>
              </motion.div>
            ) : null
          )}
        </AnimatePresence>

        {/* Prev / Next Arrows */}
        <button
          onClick={prevHeroSlide}
          className="absolute left-4 top-1/2 -translate-y-1/2 text-white bg-black/40 hover:bg-black/60 rounded-full p-2.5 z-20 transition"
          aria-label="Previous slide"
        >
          <ChevronLeft size={20} />
        </button>
        <button
          onClick={nextHeroSlide}
          className="absolute right-4 top-1/2 -translate-y-1/2 text-white bg-black/40 hover:bg-black/60 rounded-full p-2.5 z-20 transition"
          aria-label="Next slide"
        >
          <ChevronRight size={20} />
        </button>

        {/* Dots */}
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 z-20">
          {activeSlides.map((_, idx) => (
            <button
              key={idx}
              onClick={() => setHeroSlide(idx)}
              className={`w-2 h-2 rounded-full transition-all duration-300 ${
                idx === heroSlide ? "bg-yellow-400 scale-125 w-4" : "bg-white/50"
              }`}
            />
          ))}
        </div>
      </section>

      {/* CATEGORIES SECTION WITH IMAGES */}
      <section className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8 border-b border-gray-100">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-black text-[#500707] tracking-tight uppercase font-serif">
            Shop by Category
          </h3>
          <button
            onClick={() => setSelectedCategory("All")}
            className="text-xs font-bold text-[#500707] hover:underline"
          >
            View All →
          </button>
        </div>
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-3">
          {categories.filter(c => c !== "All").slice(0, 12).map((cat) => {
            const catImg = getCategoryImage(cat);
            return (
              <button
                key={cat}
                onClick={() => setSelectedCategory(cat)}
                className={`flex flex-col items-center gap-2 p-3 rounded-2xl border transition-all duration-200 ${
                  selectedCategory === cat
                    ? "bg-white border-[#500707] shadow-md scale-105"
                    : "bg-white border-gray-200 hover:border-gray-300 hover:shadow-sm"
                }`}
              >
                <div className="w-16 h-16 rounded-xl overflow-hidden bg-orange-50 border border-orange-100 flex items-center justify-center">
                  {catImg ? (
                    <img 
                      src={catImg} 
                      alt={cat}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  ) : (
                    <span className="text-2xl">{getCategoryEmoji(cat)}</span>
                  )}
                </div>
                <span className="text-[10px] font-black uppercase text-gray-700 tracking-wider text-center leading-tight">
                  {cat}
                </span>
              </button>
            );
          })}
        </div>
      </section>

      {/* CORE CONTENT LAYOUT: GRID WITH SIDEBAR */}
      <section id="products" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          
          {/* Left Column: Product Grid */}
          <div className="lg:col-span-9 space-y-6">
            
            {/* Title / Controls Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b pb-4">
              <h3 className="text-xl font-black text-[#500707] tracking-tight uppercase font-serif">
                {selectedCategory === "All" ? "Best Selling Products" : selectedCategory}
              </h3>
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-400 font-bold uppercase">View Mode:</span>
                <div className="flex bg-gray-100 p-0.5 rounded-lg border">
                  <button
                    onClick={() => setViewMode("grid")}
                    className={`px-2.5 py-1.5 rounded-md text-[10px] font-black uppercase transition-all ${
                      viewMode === "grid" ? "bg-white text-[#500707] shadow-xs" : "text-gray-500"
                    }`}
                  >
                    Grid
                  </button>
                  <button
                    onClick={() => setViewMode("table")}
                    className={`px-2.5 py-1.5 rounded-md text-[10px] font-black uppercase transition-all ${
                      viewMode === "table" ? "bg-white text-[#500707] shadow-xs" : "text-gray-500"
                    }`}
                  >
                    Table
                  </button>
                </div>
              </div>
            </div>

            {isLoading && <p className="text-center py-12 text-sm text-gray-400 font-bold">Loading Sivakasi catalogs...</p>}
            {error && <p className="text-center py-12 text-sm text-red-600 font-bold">⚠️ {error}</p>}

            {!isLoading && !error && filteredProducts.length > 0 && (
              <>
                {viewMode === "grid" ? (
                  <div className="space-y-12">
                    {filteredProducts.map((product) => {
                      const subItems = product.subProducts || product.subproducts || [];
                      if (subItems.length === 0) return null;

                      return (
                        <div key={product.pro_id} className="space-y-4">
                          <div className="border-l-4 border-[#500707] pl-3 flex items-center justify-between">
                            <h4 className="text-lg sm:text-xl font-black text-gray-800 capitalize font-serif">
                              {product.name}
                            </h4>
                            <span className="text-[10px] bg-red-50 text-red-700 px-3 py-1 rounded-full font-black uppercase tracking-wider">
                              Flat {subItems[0]?.rateEntities?.[0]?.Discount || subItems[0]?.rateentities?.[0]?.Discount || 80}% OFF
                            </span>
                          </div>

                          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 sm:gap-6">
                            {subItems.map((subProduct) => {
                              const rates = subProduct.rateEntities || subProduct.rateentities || [];
                              const rateObj = rates[0] || {};
                              const rate = rateObj.rate || 0;
                              const discount = rateObj.Discount !== undefined ? rateObj.Discount : (rateObj.discount || 0);
                              const quantityValue = rateObj.quantity || "Pack of 10";
                              const discountMultiplier = 1 - (discount / 100 || 0);
                              const discountRate = Math.round(rate * discountMultiplier);
                              const cartQuantity = cartItems.find(
                                (item) => item.proId === product.pro_id && item.subId === subProduct.sub_id
                              )?.quantity || 0;

                              return (
                                <div
                                  key={subProduct.sub_id}
                                  className="bg-white rounded-2xl overflow-hidden border border-gray-150 flex flex-col justify-between hover:shadow-md transition-all duration-300 relative group"
                                >
                                  {/* Wishlist Heart Icon */}
                                  <button
                                    onClick={() => toggleWishlist(subProduct.sub_id)}
                                    className={`absolute top-3 right-3 z-20 p-1.5 rounded-full shadow-xs transition-colors duration-200 ${
                                      wishlist.includes(subProduct.sub_id)
                                        ? "bg-red-50 text-red-500"
                                        : "bg-white/80 text-gray-400 hover:text-red-500"
                                    }`}
                                    title={wishlist.includes(subProduct.sub_id) ? "Remove from Wishlist" : "Add to Wishlist"}
                                  >
                                    <Heart size={15} fill={wishlist.includes(subProduct.sub_id) ? "currentColor" : "none"} />
                                  </button>

                                  {/* Discount badge */}
                                  {discount > 0 && (
                                    <span className="absolute top-3 left-3 bg-red-600 text-white text-[10px] font-black px-2.5 py-1 rounded-lg z-20 shadow">
                                      {discount}% OFF
                                    </span>
                                  )}

                                  {/* Product image container */}
                                  <div className="relative aspect-square overflow-hidden bg-gray-50 border-b border-gray-100 flex items-center justify-center">
                                    <img
                                      src={subProduct.childimg || "/placeholder.jpg"}
                                      alt={subProduct.subName || "Product"}
                                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                                      loading="lazy"
                                    />
                                  </div>

                                  {/* Description & Cart Adjuster */}
                                  <div className="p-3.5 flex-1 flex flex-col justify-between gap-3">
                                    <div className="space-y-1 text-left">
                                      <p className="font-extrabold text-gray-800 text-sm line-clamp-2 min-h-[2.5rem]">
                                        {subProduct.subName || "Product"}
                                      </p>
                                      <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">
                                        {quantityValue}
                                      </p>
                                      
                                      {/* Stars Review */}
                                      <div className="flex items-center gap-1 text-yellow-500 text-xs">
                                        <span>★★★★★</span>
                                        <span className="text-[10px] text-gray-400 font-bold">({120 + (subProduct.sub_id % 30)})</span>
                                      </div>
                                    </div>

                                    {/* Pricing & Button block */}
                                    <div className="space-y-3">
                                      <div className="flex items-baseline gap-2">
                                        <span className="text-base font-extrabold text-green-700">₹{discountRate}</span>
                                        {discount > 0 && (
                                          <span className="text-xs line-through text-gray-400 font-semibold">₹{rate}</span>
                                        )}
                                      </div>

                                      {cartQuantity > 0 ? (
                                        <div className="flex items-center justify-between bg-red-50 border border-red-200 rounded-xl p-0.5">
                                          <button
                                            onClick={() => handleQuantityDecrease(product.pro_id, subProduct.sub_id)}
                                            className="w-8 h-8 rounded-lg bg-white border text-red-700 font-black hover:bg-red-50 flex items-center justify-center transition text-sm shadow-xs"
                                            disabled={isUpdating}
                                          >
                                            -
                                          </button>
                                          <input
                                            type="number"
                                            min="0"
                                            value={cartQuantity}
                                            onChange={(e) => handleQuantityChange(product.pro_id, subProduct.sub_id, e.target.value)}
                                            className="w-12 text-center font-extrabold text-sm text-[#500707] bg-transparent border-0 focus:ring-0 focus:outline-none p-0 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                          />
                                          <button
                                            onClick={(e) => handleQuantityIncrease(product.pro_id, subProduct.sub_id, e)}
                                            className="w-8 h-8 rounded-lg bg-white border text-red-700 font-black hover:bg-red-50 flex items-center justify-center transition text-sm shadow-xs"
                                            disabled={isUpdating}
                                          >
                                            +
                                          </button>
                                        </div>
                                      ) : (
                                        <div className="flex gap-2">
                                          <button
                                            onClick={(e) => handleQuantityIncrease(product.pro_id, subProduct.sub_id, e)}
                                            className="flex-1 bg-[#500707] hover:bg-[#6f0d0d] text-white py-2.5 rounded-xl text-xs font-black uppercase tracking-wider transition shadow-sm hover:shadow"
                                          >
                                            Add To Cart
                                          </button>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="overflow-x-auto rounded-2xl border border-gray-200 shadow-xs bg-white">
                    <table className="w-full border-collapse min-w-[600px] text-left">
                      <thead>
                        <tr className="bg-[#500707] text-white text-xs font-black uppercase tracking-wider">
                          <th className="p-3 border">Image</th>
                          <th className="p-3 border">Product Item</th>
                          <th className="p-3 border">Specification</th>
                          <th className="p-3 border">Wholesale Rate</th>
                          <th className="p-3 border text-center">Add Qty</th>
                          <th className="p-3 border">Subtotal</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredProducts.map((product) => (
                          <React.Fragment key={product.pro_id}>
                            <tr className="bg-red-50/50">
                              <td colSpan="6" className="p-3 border font-black text-xs text-[#500707] uppercase tracking-wider">
                                🎇 {product.name}
                              </td>
                            </tr>
                            {(product.subProducts || product.subproducts || []).map((subProduct) => {
                              const rates = subProduct.rateEntities || subProduct.rateentities || [];
                              const rateObj = rates[0] || {};
                              const rate = rateObj.rate || 0;
                              const discount = rateObj.Discount !== undefined ? rateObj.Discount : (rateObj.discount || 0);
                              const quantityValue = rateObj.quantity || "N/A";
                              const discountMultiplier = 1 - (discount / 100 || 0);
                              const discountRate = Math.round(rate * discountMultiplier);
                              const cartQuantity = cartItems.find(
                                (item) => item.proId === product.pro_id && item.subId === subProduct.sub_id
                              )?.quantity || 0;
                              const total = discountRate * cartQuantity;

                              return (
                                <tr key={`${product.pro_id}-${subProduct.sub_id}`} className="border-b hover:bg-gray-50/30 transition text-xs font-semibold text-gray-700">
                                  <td className="p-3 border">
                                    <img
                                      src={subProduct.childimg || "/placeholder.jpg"}
                                      alt={subProduct.subName || "Product"}
                                      className="w-10 h-10 object-cover rounded-lg border"
                                    />
                                  </td>
                                  <td className="p-3 border font-extrabold text-gray-800">
                                    <div className="flex items-center justify-between gap-2">
                                      <span>{subProduct.subName}</span>
                                      <button 
                                        onClick={() => toggleWishlist(subProduct.sub_id)}
                                        className={`p-1 rounded-full transition-colors duration-200 ${
                                          wishlist.includes(subProduct.sub_id) ? "text-red-500 bg-red-50" : "text-gray-350 hover:text-red-500"
                                        }`}
                                        title={wishlist.includes(subProduct.sub_id) ? "Remove from Wishlist" : "Add to Wishlist"}
                                      >
                                        <Heart size={13} fill={wishlist.includes(subProduct.sub_id) ? "currentColor" : "none"} />
                                      </button>
                                    </div>
                                  </td>
                                  <td className="p-3 border text-gray-400 font-bold uppercase tracking-wider">
                                    {quantityValue}
                                  </td>
                                  <td className="p-3 border">
                                    <span className="line-through text-gray-400 mr-1.5">₹{rate}</span>
                                    <span className="text-green-700 font-extrabold">₹{discountRate}</span>
                                  </td>
                                  <td className="p-3 border text-center">
                                    <div className="inline-flex items-center gap-1.5 bg-gray-55/40 bg-gray-50 p-0.5 border rounded-lg">
                                      <button
                                        onClick={() => handleQuantityDecrease(product.pro_id, subProduct.sub_id)}
                                        className="w-6 h-6 rounded bg-white border text-red-800 font-black hover:bg-red-50 flex items-center justify-center disabled:opacity-50 shadow-xs"
                                        disabled={cartQuantity <= 0 || isUpdating}
                                      >
                                        -
                                      </button>
                                      <input
                                        type="number"
                                        min="0"
                                        value={cartQuantity}
                                        onChange={(e) => handleQuantityChange(product.pro_id, subProduct.sub_id, e.target.value)}
                                        className="w-10 text-center font-black text-[#500707] bg-transparent border-0 focus:ring-0 focus:outline-none p-0 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                      />
                                      <button
                                        onClick={(e) => handleQuantityIncrease(product.pro_id, subProduct.sub_id, e)}
                                        className="w-6 h-6 rounded bg-white border text-red-800 font-black hover:bg-red-50 shadow-xs"
                                        disabled={isUpdating}
                                      >
                                        +
                                      </button>
                                    </div>
                                  </td>
                                  <td className="p-3 border font-black text-green-700">
                                    ₹{total}
                                  </td>
                                </tr>
                              );
                            })}
                          </React.Fragment>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}

            {!isLoading && !error && filteredProducts.length === 0 && (
              <p className="text-center py-12 text-sm text-gray-400 font-bold">No matching products found.</p>
            )}

          </div>

          {/* Right Column: Sidebar Panels */}
          <div className="lg:col-span-3 space-y-6">
            
            {/* Combo Offers Box */}
            <div className="bg-gradient-to-br from-[#1C0526] to-[#390A4D] rounded-3xl p-6 text-white text-center shadow-lg relative overflow-hidden flex flex-col justify-between min-h-[300px]">
              <div className="space-y-2 text-left">
                <p className="text-[10px] text-yellow-300 font-black tracking-widest uppercase">Combo Offers</p>
                <h4 className="text-2xl font-black font-serif leading-none tracking-tight">Save More<br />Buy More!</h4>
              </div>
              
              <div className="text-5xl py-4 select-none animate-bounce">
                🎁
              </div>

              <div className="space-y-4">
                <button 
                  onClick={() => { setSelectedCategory("Combo Packs"); navigate("/"); }}
                  className="w-full bg-yellow-400 hover:bg-yellow-500 text-red-950 font-black py-2.5 rounded-full text-xs uppercase tracking-wider transition shadow-md"
                >
                  Shop Now
                </button>
              </div>
            </div>

            {/* Why Choose Box */}
            <div className="bg-[#FFFDF4] rounded-3xl p-6 border border-yellow-200/60 shadow-xs text-left">
              <h4 className="text-sm font-black text-gray-800 uppercase tracking-wider mb-4 border-b border-yellow-200/50 pb-2">
                Why Choose Us?
              </h4>
              <ul className="space-y-3.5 text-xs font-bold text-gray-605">
                <li className="flex items-center gap-2">
                  <span className="text-green-600 text-sm">✔️</span>
                  <span>Trusted by 10,000+ Customers</span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-green-600 text-sm">✔️</span>
                  <span>Premium Quality Products</span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-green-600 text-sm">✔️</span>
                  <span>Best Prices Guaranteed</span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-green-600 text-sm">✔️</span>
                  <span>Safe & Secure Shopping</span>
                </li>
              </ul>
            </div>

            {/* Bulk Order CTA */}
            <div className="bg-gradient-to-br from-blue-900 to-blue-950 rounded-3xl p-6 text-white text-center shadow-lg relative overflow-hidden">
              <div className="space-y-2 text-left">
                <p className="text-[10px] text-yellow-300 font-black tracking-widest uppercase">Bulk Orders</p>
                <h4 className="text-xl font-black font-serif leading-none tracking-tight">Wholesale<br />Pricing!</h4>
              </div>
              <div className="text-4xl py-3 select-none">
                📦
              </div>
              <a
                href="https://wa.me/919728622667?text=Hi!%20I%20want%20to%20inquire%20about%20bulk%20crackers%20order."
                target="_blank"
                rel="noopener noreferrer"
                className="block w-full bg-green-500 hover:bg-green-600 text-white font-black py-2.5 rounded-full text-xs uppercase tracking-wider transition shadow-md"
              >
                <FaWhatsapp size={14} className="inline mr-1.5" />
                Contact for Bulk
              </a>
            </div>

          </div>

        </div>
      </section>

      {/* Floating Sticky Bottom Cart Summary Overlay Bar */}
      {cartItems.length > 0 && (
        <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 z-40 w-[90%] max-w-md bg-white/95 backdrop-blur-md border border-red-200 rounded-2xl shadow-xl px-5 py-3 flex items-center justify-between gap-4 animate-slide-in-bottom">
          <div className="space-y-0.5 text-left">
            <p className="text-[10px] text-gray-500 font-bold uppercase tracking-wider">
              Selected: <span className="text-red-900">{cartItems.reduce((sum, item) => sum + (item.quantity || 0), 0)} items</span>
            </p>
            <p className="text-sm sm:text-base font-extrabold text-red-900 leading-none">
              Total: ₹{Math.round(cartItems.reduce((sum, item) => sum + (item.rate || 0) * (item.quantity || 0) * (1 - (item.discount || 0) / 100), 0))}
            </p>
          </div>
          <button
            onClick={() => setIsCartOpen(true)}
            className="bg-[#500707] hover:bg-[#6c0c0c] text-white font-black py-2.5 px-5 rounded-xl text-xs uppercase tracking-wider shadow transition flex items-center gap-1.5"
          >
            <span>🛒</span> View Cart
          </button>
        </div>
      )}

      {/* DRAWERS & DIALOGS */}
      <Cart isOpen={isCartOpen} onClose={() => setIsCartOpen(false)} />
      <Wishlist isOpen={isWishlistOpen} onClose={() => setIsWishlistOpen(false)} />
      <QuickShop isOpen={isQuickShopOpen} onClose={() => setIsQuickShopOpen(false)} />

      {/* ===== FLOATING QUICK SHOP BUTTON (Bottom-Right) - Opens Quick Shop Panel ===== */}
      <div className="fixed z-50 bottom-24 right-6 flex flex-col items-center gap-3">
        <div 
          onClick={() => setIsQuickShopOpen(true)}
          className="flex flex-col items-center text-center cursor-pointer relative"
        >
          <span className="absolute inline-flex h-14 w-14 rounded-full bg-blue-400 opacity-75 animate-ping"></span>
          <div className="relative bg-white hover:bg-blue-100 border border-blue-500 shadow-lg p-3 rounded-full transition-colors duration-200 z-10 quick-shop-pulse">
            <ShoppingCart size={22} className="text-blue-600" />
          </div>
          <span className="text-sm bg-red-500 text-white px-2 py-1 mt-1 rounded-full animate-bounce font-bold">
            Quick Shop
          </span>
        </div>
      </div>

      {/* ===== DRAGGABLE WHATSAPP FLOATING BUTTON ===== */}
      <WhatsAppFloatingButton />

      {/* ===== SOCIAL SHARE FLOATING BAR (Left Side) ===== */}
      <div className="fixed z-50 left-3 top-1/2 -translate-y-1/2 flex flex-col gap-2">
        <a href="https://wa.me/919728622667" target="_blank" rel="noopener noreferrer"
           className="social-share-btn bg-green-500 text-white hover:bg-green-600 shadow-md">
          <FaWhatsapp size={14} /> WhatsApp
        </a>
        <a href="https://www.facebook.com/sharer/sharer.php?u=https://mayavacrackers.com" target="_blank" rel="noopener noreferrer"
           className="social-share-btn bg-blue-600 text-white hover:bg-blue-700 shadow-md">
          <FaFacebook size={14} /> Share
        </a>
        <a href="https://www.instagram.com/" target="_blank" rel="noopener noreferrer"
           className="social-share-btn bg-pink-600 text-white hover:bg-pink-700 shadow-md">
          <FaInstagram size={14} /> Follow
        </a>
      </div>
    </div>
  );
};

// ===== DRAGGABLE WHATSAPP FLOATING BUTTON COMPONENT =====
const WhatsAppFloatingButton = () => {
  const [position, setPosition] = useState({ x: window.innerWidth - 80, y: window.innerHeight - 100 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const btnRef = useRef(null);

  useEffect(() => {
    setPosition({ x: window.innerWidth - 80, y: window.innerHeight - 100 });
  }, []);

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isDragging) return;
      setPosition({
        x: Math.max(0, Math.min(e.clientX - dragStart.x, window.innerWidth - 60)),
        y: Math.max(0, Math.min(e.clientY - dragStart.y, window.innerHeight - 60))
      });
    };
    const handleMouseUp = () => setIsDragging(false);
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging, dragStart]);

  const handleMouseDown = (e) => {
    const rect = btnRef.current.getBoundingClientRect();
    setDragStart({ x: e.clientX - rect.left, y: e.clientY - rect.top });
    setIsDragging(true);
  };

  const handleClick = () => {
    const phone = "919728622667";
    const msg = encodeURIComponent("Hi! I want to inquire about your crackers products.");
    window.open(`https://wa.me/${phone}?text=${msg}`, "_blank");
  };

  return (
    <div
      ref={btnRef}
      onMouseDown={handleMouseDown}
      onClick={handleClick}
      style={{
        position: "fixed",
        left: position.x + "px",
        top: position.y + "px",
        zIndex: 9999,
        cursor: isDragging ? "grabbing" : "grab",
      }}
      className="btn-gradient p-3.5 rounded-full shadow-xl flex items-center justify-center animate-bounce"
      title="Chat with us on WhatsApp"
    >
      <FaWhatsapp size={28} />
    </div>
  );
};

export default HomePage;
